import React, { ErrorInfo } from 'react';
import { ClientLoggingService } from '../../services';
import { LoggingLevel, LoggingMessage } from '@nt-portal/server-sdk';
import { store } from '../../redux/store';
import { showMessageBox, MessageType } from '../../redux/message-box.slice';
import { RedirectClient } from '@azure/msal-browser/dist/internals';
import { Navigate } from 'react-router';
import { FabrixIcon, translate } from '../../common';
import { Button } from 'primereact/button';
import { useIcon } from '../../hooks';
import Footer from '../footer';

export interface ErrorBoundaryProps {
  children: React.ReactNode
}

export interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

/**
 * Captures uncaught errors during render and displays a fallback error UI while logging client diagnostics.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {


  private _logService: ClientLoggingService;
  private static isErrorHandlerSet = false;
 
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, message: "" };
    this._logService = new ClientLoggingService();
  }

  /**
   * Handles global window errors, logging them and surfacing a notification toast.
   * @param event Error event emitted from window.
   */
  private errorHandler = (event: ErrorEvent) => {

    let { message, filename, error } = event;

    let msg = typeof message === 'string' ? message : ""
    if (!msg && error) {
      msg = error.message;
    }

    let loggingMessage: LoggingMessage = {
      area: filename,
      source: "client",
      message: msg,
      level: LoggingLevel.Error,
      timestamp: new Date(),
      userId: '',
      createdBy: '',
      metaData: error?.stack
    }

    this._logService.add({ item: loggingMessage });
    store.dispatch(showMessageBox({ message: msg, messageType: MessageType.Error, duration: 5000, position: "top-center" }))
    this.setState({ hasError: true, message: msg });


    event.preventDefault();

  };

  componentDidMount() {

    if (ErrorBoundary.isErrorHandlerSet) return;
    ErrorBoundary.isErrorHandlerSet = true;

    window.addEventListener("error", this.errorHandler, { once: true });

  }

  componentWillUnmount() {
    window.removeEventListener("error", this.errorHandler);
  }

  /**
   * React lifecycle handler capturing errors thrown during rendering or lifecycle methods.
   * @param error Raised exception object.
   * @param info Additional component stack metadata.
   */
  componentDidCatch(error: Error, info: ErrorInfo) {

    this.setState({ hasError: true, message: error.message });

    let loggingMessage: LoggingMessage = {
      area: "client",
      source: "client",
      message: error.message,
      level: LoggingLevel.Error,
      timestamp: new Date(),
      userId: '',
      createdBy: '',
      metaData: error.stack
    }

    this._logService.add(loggingMessage)
    store.dispatch(showMessageBox({ message: error.message, messageType: MessageType.Error, duration: 5000, position: "top-center" }))
  }

  render() {
    if (this.state.hasError) {
      let iconState = store.getState().icons as any;
      let iconClassName = iconState[FabrixIcon.home];
      return (
        <div className="fbx-full-height">
          <div className="flex flex-column justify-content-center align-items-center fbx-error-page">
            <div className="flex fbx-error-page-heading">{translate("ERROR_PAGE_HEADING")}</div>
            <div className="flex fbx-error-page-title">{translate("APPLICATION_ERROR")}</div>
            <div className="flex fbx-error-page-message">{this.state.message}</div>
            <Button label={translate("HOME")} icon={iconClassName} onClick={() => window.location.href = "/"} className="error-page-button p-button-outlined fbx-button-outlined" />


          </div>
          <Footer />
        </div>)
    }
    else {
      return this.props.children;
    }
  }
}




export default (ErrorBoundary)